'use client'

import {useId, useState} from 'react'
import {HelpTooltipButton} from './HelpTooltipButton'

export interface PassAccessClass {
    name: string
    colorKey: string | null
}

export interface PassAccessSummary {
    groupMode: string
    groupClasses: PassAccessClass[]
    individualClasses: PassAccessClass[]
}
export interface PassAccessLabels {
    group: string
    individual: string
    all: string
    selected: (count: number) => string
}

function PassAccessList({title, items, colorToken}: {title?: string; items: PassAccessClass[]; colorToken: (key: string) => string}) {
    return <span className="block text-left">
        {title ? <span className="mb-2 block border-b border-gray-200 pb-2 text-sm font-semibold text-gray-900 dark:border-gray-700 dark:text-gray-100">{title}</span> : null}
        <span className="block space-y-1">
            {items.map(({name, colorKey}, index) => <span key={`${name}-${index}`} className="relative block min-w-0 whitespace-normal [overflow-wrap:anywhere] py-1.5 pl-3 text-sm font-medium leading-5 text-gray-700 dark:text-gray-200">
                <span aria-hidden className="absolute bottom-1.5 left-0 top-1.5 w-0.5 rounded-full" style={{backgroundColor: colorKey ? colorToken(colorKey) : 'var(--class-color-accent-white)'}}/>{name}
            </span>)}
        </span>
    </span>
}

export function PassAccess({summary, labels, expanded = false, size = 'compact', layout = 'columns', colorToken}: {summary?: PassAccessSummary | null; labels: PassAccessLabels; expanded?: boolean; size?: 'compact' | 'regular'; layout?: 'columns' | 'responsive' | 'metadata'; colorToken: (key: string) => string}) {
    const [open, setOpen] = useState<string | null>(null)
    const id = useId()
    if (!summary) return null
    const scopes = [
        {key: 'group', title: labels.group, items: summary.groupClasses, all: summary.groupMode === 'all'},
        {key: 'individual', title: labels.individual, items: summary.individualClasses, all: false},
    ].filter(scope => scope.items.length > 0 || scope.all)
    if (!scopes.length) return null
    return <div data-pass-access data-expanded={expanded} className={`grid min-w-0 items-center gap-y-2 ${layout === 'metadata' ? 'gap-x-3 sm:gap-x-12' : 'gap-x-3'} ${!expanded && scopes.length === 2 ? (layout === 'responsive' ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-2') : 'grid-cols-1'}`}>
        {scopes.map(scope => <div key={scope.key} className="min-w-0 text-left">
            {expanded ? <PassAccessList title={scope.title} items={scope.items.length ? scope.items : [{name: labels.all, colorKey: null}]} colorToken={colorToken}/> :
                <div className={`flex min-w-0 ${layout === 'metadata' ? 'flex-col items-start' : 'items-center justify-start'} gap-1 ${size === 'regular' ? 'text-sm' : 'text-xs'}`}>
                <span className="min-w-0 truncate text-gray-500 dark:text-gray-400">{scope.title}:</span>
                <HelpTooltipButton micro={false} align="start" ariaLabel={`${scope.title}: ${scope.all ? labels.all : labels.selected(scope.items.length)}`}
                    tooltipId={`${id}-${scope.key}`} open={open === scope.key} onToggle={() => setOpen(open === scope.key ? null : scope.key)}
                    maxTooltipHeight={280}
                    description={<PassAccessList items={scope.items.length ? scope.items : [{name: labels.all, colorKey: null}]} colorToken={colorToken}/>}
                    className="inline-flex min-w-0 shrink-0 max-w-full"
                    buttonClassName={`!h-auto min-h-10 !w-auto min-w-0 !max-w-full !justify-start !rounded-md !border-0 !bg-transparent !px-0 !py-1 ${size === 'regular' ? '!text-sm !font-medium' : '!text-xs !font-normal'} shadow-none`}
                    icon={<span className="flex min-w-0 items-center gap-1 text-left">
                        <span data-pass-access-label className="min-w-0 truncate">
                            <span className="font-medium text-primary-700 underline decoration-dotted underline-offset-4 dark:text-primary-300">{scope.all ? labels.all : labels.selected(scope.items.length)}</span>
                        </span>
                        <span aria-hidden className="shrink-0 text-primary-600">ⓘ</span>
                    </span>}/>
                </div>
            }
        </div>)}
    </div>
}
